import { useEffect, useState } from 'react';
import { Avatar, Button, Card, FieldError, Input, Label, TextField } from '@heroui/react';

import Notice from '../../common/Notice';
import useValidation from '../../common/useValidation';
import SuggestionsField from '../../common/SuggestionsField';
import userService from '../../../services/auth.service';
import { initialsOf } from '../../../services/initials';
import * as check from '../../../services/validation';
// feat/email-change
import FieldWithAction from '../../common/FieldWithAction';
import EmailChangeDialog from './EmailChangeDialog';
import { cancelEmailChange } from '../../../services/emailChange.service';
import { messageOf, EMAIL_CHANGE } from '../../../i18n/el';

// ΛΑ-02: προβολή και ενημέρωση του προφίλ. Το όνομα χρήστη και η διεύθυνση
// ηλεκτρονικού ταχυδρομείου εμφανίζονται αλλά **δεν** ενημερώνονται: το
// `PATCH /api/auth/me` δεν δέχεται τα δύο πεδία, οπότε τα αντίστοιχα πεδία της
// φόρμας είναι ανενεργά αντί να δίνουν την ψευδαίσθηση ότι αποθηκεύονται.
//
// Η κατάσταση του προφίλ ζει στο `Dashboard`, γιατί τη χρειάζεται και η γραμμή
// πλοήγησης. Εδώ φτάνει ως props, όπως και πριν.

export default function ProfilePage({
    username, email, pendingEmail, fullName, avatar, interestsText,
    error, successMsg, fieldErrors,
    handleChange, handleInterestsChange, setInterestsText,
    userDataUpdate, fetchUserData, resetMessages
}) {
    const [suggestions, setSuggestions] = useState([]);
    // feat/email-change (ΛΑ-02 / D45).
    //
    // **Δύο κομμάτια κατάστασης και όχι ένα.** Το `isChanging` ανοίγει τον
    // διάλογο —σύμβαση **D26**: ο γονέας αποδίδει υπό συνθήκη— και το `pending`
    // κρατά τη **μασκαρισμένη** διεύθυνση ώστε η ένδειξη να εμφανιστεί αμέσως,
    // χωρίς δεύτερη ανάγνωση του προφίλ. Αρχικοποιείται από το prop και
    // ενημερώνεται τοπικά: η οθόνη πρέπει να λέει την αλήθεια **μετά** το
    // αίτημα, όχι μετά την επόμενη φόρτωση.
    const [isChanging, setChanging] = useState(false);
    const [pending, setPending] = useState(pendingEmail ?? null);
    const [changeNotice, setChangeNotice] = useState('');
    const [changeError, setChangeError] = useState('');
    const initials = initialsOf({ fullName, username });

    // feat/email-change — **ονομασμένοι χειριστές και όχι βέλη μέσα στο JSX**,
    // και ο λόγος δεν είναι αισθητικός: ο στατικός φράχτης καλωδίωσης διαβάζει
    // τις ιδιότητες ενός στοιχείου μέχρι το πρώτο `>`, οπότε ένα `=>` μέσα σε
    // ιδιότητα **κόβει** την ανάγνωση και ό,τι ακολουθεί αναφέρεται ως
    // λείπον. Ήταν ψευδές θετικό — και ο ίδιος ο φράχτης γράφει ότι φράχτης με
    // θόρυβο παύει να διαβάζεται. Η ονομασία το λύνει χωρίς να τον αγγίξει.
    function openEmailChange() {
        setChangeError('');
        setChanging(true);
    }

    function closeEmailChange() {
        setChanging(false);
    }

    function handleEmailChangeRequested(masked) {
        setChanging(false);
        setPending(masked);
        setChangeNotice(EMAIL_CHANGE.requested);
    }

    function clearChangeNotice() {
        setChangeNotice('');
    }

    function handleCancelEmailChange() {
        setChangeError('');
        cancelEmailChange().then(
            () => {
                setPending(null);
                setChangeNotice(EMAIL_CHANGE.cancelled);
            },
            (apiError) => setChangeError(messageOf(apiError))
        );
    }
    const validation = useValidation();

    // Ο διακομιστής απαιτεί 2 χαρακτήρες στο ονοματεπώνυμο και **έγκυρη απόλυτη
    // διεύθυνση** στην εικόνα. Και τα δύο περνούσαν τοπικά με οτιδήποτε μη κενό.
    const nameMessage = fieldErrors.fullName ?? validation.show('fullName', check.fullName(fullName));
    const avatarMessage = fieldErrors.avatar ?? validation.show('avatar', check.optionalUrl(avatar));

    useEffect(() => {
        resetMessages();
        fetchUserData();

        userService.getInterestSuggestions().then(setSuggestions, () => setSuggestions([]));
        // Εκτελείται μία φορά στην είσοδο στην οθόνη. Οι τρεις συναρτήσεις είναι
        // δεμένες μέθοδοι του Dashboard και δεν αλλάζουν ταυτότητα.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Οι μέθοδοι του `Dashboard` δέχονται γεγονός DOM, ενώ τα πεδία του HeroUI
    // δίνουν την τιμή. Η προσαρμογή γίνεται εδώ, ώστε να μην αλλάξει το κέλυφος.
    const asEvent = (name) => (value) => handleChange({ target: { name, value } });

    /**
     * Προσθέτει προτεινόμενο ενδιαφέρον στο πεδίο. Ο στόχος είναι η σύγκλιση της
     * ορολογίας μεταξύ ερευνητών, ώστε τα φίλτρα του P1-5 να βρίσκουν όντως ομάδες.
     */
    /**
     * Ό,τι έχει ήδη γραφτεί στο πεδίο, ως πίνακας. Το πεδίο κρατά **κείμενο με
     * κόμματα** — δεν αλλάζει εδώ — και οι προτάσεις χρειάζονται τιμές: η
     * μετατροπή γίνεται σε **ένα** σημείο αντί σε δύο.
     */
    const chosenInterests = interestsText.split(',').map((item) => item.trim()).filter(Boolean);

    function addInterest(value) {
        if (chosenInterests.includes(value)) return;

        setInterestsText([...chosenInterests, value].join(', '));
    }

    function handleSubmit(event) {
        event.preventDefault();
        validation.markSubmitted();

        // Η υποβολή σταματά εδώ: το Dashboard δεν γνωρίζει τους κανόνες των πεδίων.
        if (check.fullName(fullName) || check.optionalUrl(avatar)) return;

        userDataUpdate();
    }

    return (
        <div className="grid gap-6 lg:grid-cols-[18rem_1fr]">
            <Card className="items-center gap-3 text-center">
                {/* Ίδιος κανόνας με την κεφαλίδα: χωρίς διεύθυνση, τα αρχικά του
                    ονόματος σε κύκλο με το χρώμα του θέματος. Το `Avatar` χειρίζεται
                    μόνο του και την **αποτυχία φόρτωσης** — μια διεύθυνση που έπαψε
                    να ισχύει πέφτει στα αρχικά, χωρίς `onError` στο σημείο χρήσης. */}
                {/* **Μεγαλύτερα αρχικά ΕΔΩ ΚΑΙ ΜΟΝΟ ΕΔΩ.** Ο κύκλος είναι επτά
                    φορές μεγαλύτερος από της κεφαλίδας ενώ τα γράμματα ήταν μιάμιση:
                    η αναλογία διαβαζόταν ως σφάλμα φόρτωσης και όχι ως αντικαταστάτης.
                    Οι **διαστάσεις της κεφαλίδας δεν αλλάζουν** — είναι μετρημένες
                    (65 και 40 πίξελ) και ελέγχονται χειροκίνητα. */}
                <Avatar className="size-28">
                    {avatar && <Avatar.Image src={avatar} alt="" />}
                    {/* **Το μέγεθος μπαίνει στον ΑΝΤΙΚΑΤΑΣΤΑΤΗ, όχι στη ρίζα.**
                        Η κλάση `text-4xl` ήταν στο `Avatar` και δεν έφτανε ποτέ στα
                        αρχικά: ο αντικαταστάτης του HeroUI ορίζει δικό του μέγεθος
                        γραμματοσειράς, που κληρονομεί μόνο ό,τι δεν δηλώνει. Το
                        αποτέλεσμα ήταν δύο γράμματα στο μέγεθος της λίστας μέσα σε
                        κύκλο 7rem — επιβεβαιωμένο σε δύο ελέγχους.

                        **Μόνο εδώ.** Η κεφαλίδα και οι κάρτες χρησιμοποιούν το
                        `UserBadge` με μικρό κύκλο, όπου το ίδιο μέγεθος θα ξεχείλιζε.
                        Η σελίδα προφίλ είναι η μόνη θέση με κύκλο αυτού του μεγέθους,
                        οπότε η τιμή ζει εδώ και δεν γίνεται σχεδιαστική σταθερά για
                        ένα σημείο χρήσης. */}
                    <Avatar.Fallback className="text-[2rem] font-semibold">{initials}</Avatar.Fallback>
                </Avatar>
                <Card.Title>{fullName}</Card.Title>
                <Card.Description>@{username}</Card.Description>
            </Card>

            <Card>
                <Card.Header><Card.Title>Στοιχεία προφίλ</Card.Title></Card.Header>
                <Card.Content>
                    <form onSubmit={handleSubmit} className="flex flex-col gap-4" noValidate>
                        <div className="grid gap-4 sm:grid-cols-2">
                            <TextField value={username ?? ''} isDisabled>
                                <Label>Όνομα χρήστη</Label>
                                <Input />
                                <span className="text-sm text-muted">Δεν αλλάζει.</span>
                            </TextField>

                            {/* feat/email-change — ΛΑ-02 / D45.
                                ⚠ **ΤΟ ΠΕΔΙΟ ΜΕΝΕΙ ΑΝΕΝΕΡΓΟ ΚΑΙ ΔΕΝ ΓΙΝΕΤΑΙ
                                ΕΠΕΞΕΡΓΑΣΙΜΟ**, κατά το πρότυπο της **D10** για
                                τη διεύθυνση πόρου: πεδίο **ανενεργό και όχι
                                κρυμμένο**, με τη διαδρομή διόρθωσης να ξεκινά
                                από την ίδια οθόνη. Η αλλαγή δεν είναι
                                αποθήκευση προφίλ — είναι δική της ροή δύο
                                βημάτων, και ένα πεδίο που δέχεται πληκτρολόγηση
                                θα υποσχόταν ότι το «Αποθήκευση» από κάτω την
                                ολοκληρώνει. */}
                            <FieldWithAction
                                action={(
                                    <Button variant="outline" onPress={openEmailChange}>
                                        {EMAIL_CHANGE.action}
                                    </Button>
                                )}
                            >
                                {() => (
                                    <TextField value={email ?? ''} isDisabled>
                                        <Label>Διεύθυνση ηλεκτρονικού ταχυδρομείου</Label>
                                        <Input />
                                        <span className="text-sm text-muted">
                                            {EMAIL_CHANGE.readOnly}
                                        </span>
                                    </TextField>
                                )}
                            </FieldWithAction>
                        </div>

                        {/* feat/email-change — η ένδειξη εκκρεμότητας.
                            ⚠ **ΕΞΩ ΑΠΟ ΤΟ ΠΛΕΓΜΑ ΤΩΝ ΔΥΟ ΣΤΗΛΩΝ**, σε πλήρες
                            πλάτος: αφορά τη διεύθυνση αλλά **δεν είναι πεδίο**,
                            και μέσα στη στήλη θα στοιχιζόταν σαν να είναι
                            μήνυμα σφάλματος του διπλανού πεδίου. */}
                        {changeError && (
                            <Notice status="danger" title={EMAIL_CHANGE.dialogTitle}>
                                {changeError}
                            </Notice>
                        )}
                        {changeNotice && (
                            <Notice
                                status="success"
                                title={EMAIL_CHANGE.dialogTitle}
                                onDismiss={clearChangeNotice}
                            >
                                {changeNotice}
                            </Notice>
                        )}
                        {pending && (
                            <div className="flex flex-wrap items-center gap-3 rounded-xl border border-border bg-accent-soft px-3 py-2">
                                <div className="flex min-w-0 flex-col">
                                    <span className="text-sm font-semibold">
                                        {EMAIL_CHANGE.pending(pending)}
                                    </span>
                                    <span className="text-sm text-muted">
                                        {EMAIL_CHANGE.pendingHint}
                                    </span>
                                </div>
                                {/* Η ακύρωση είναι η **ασφαλής** κατεύθυνση —
                                    επαναφέρει την κατάσταση πριν από το αίτημα—
                                    οπότε δεν ζητά κωδικό και δεν ζητά
                                    επιβεβαίωση: προειδοποίηση πριν από ενέργεια
                                    που δεν βλάπτει κανέναν είναι θόρυβος
                                    (ΜΛΑ-03). */}
                                <Button
                                    variant="outline"
                                    className="ms-auto"
                                    onPress={handleCancelEmailChange}
                                >
                                    {EMAIL_CHANGE.cancelRequest}
                                </Button>
                            </div>
                        )}

                        <TextField
                            value={fullName ?? ''}
                            onChange={asEvent('fullName')}
                            onBlur={validation.touch('fullName')}
                            isInvalid={Boolean(nameMessage)}
                            isRequired
                        >
                            <Label>Ονοματεπώνυμο</Label>
                            <Input />
                            <FieldError>{nameMessage}</FieldError>
                        </TextField>

                        <TextField
                            value={avatar ?? ''}
                            onChange={asEvent('avatar')}
                            onBlur={validation.touch('avatar')}
                            isInvalid={Boolean(avatarMessage)}
                        >
                            <Label>Εικόνα προφίλ</Label>
                            <Input placeholder="https://..." />
                            <span className="text-sm text-muted">Πλήρης διεύθυνση εικόνας.</span>
                            <FieldError>{avatarMessage}</FieldError>
                        </TextField>

                        <TextField
                            value={interestsText ?? ''}
                            onChange={(value) => handleInterestsChange({ target: { value } })}
                            isInvalid={Boolean(fieldErrors.interests)}
                        >
                            <Label>Ερευνητικά ενδιαφέροντα</Label>
                            <Input placeholder="π.χ. ανανεώσιμη ενέργεια, digital humanities" />
                            <span className="text-sm text-muted">
                                Χωρίστε τα με κόμματα. Οι πολυλεκτικοί όροι κρατούν τα κενά τους.
                            </span>
                            <FieldError>{fieldErrors.interests}</FieldError>
                        </TextField>

                        {/* D49: το μπλοκ που ήταν γραμμένο εδώ έγινε **κοινό
                            συστατικό**, αυτούσιο. Η οθόνη δεν έχασε τίποτε και οι
                            ετικέτες κέρδισαν τις προτάσεις που δεν είχαν ποτέ. */}
                        <SuggestionsField
                            suggestions={suggestions}
                            value={chosenInterests}
                            onAdd={addInterest}
                            label="Ενδιαφέροντα που χρησιμοποιούνται ήδη"
                            hint="Χρησιμοποιούνται ήδη στο σύστημα. Πατήστε για προσθήκη:"
                        />

                        {error && <Notice status="danger" title="Η αποθήκευση απέτυχε">{error}</Notice>}
                        {successMsg && (
                            <Notice status="success" title="Αποθηκεύτηκε" onDismiss={resetMessages}>
                                {successMsg}
                            </Notice>
                        )}

                        <Button type="submit" className="self-start">Ενημέρωση προφίλ</Button>
                    </form>
                </Card.Content>
            </Card>

            {/* feat/email-change — **σύμβαση D26**: το `isOpen` δίνεται σκέτο
                μέσα στον διάλογο και ο γονέας τον αποδίδει **υπό συνθήκη**, ώστε
                καμία πηγή κλεισίματος εκτός της βιβλιοθήκης να μην τον αφήνει
                ανοιχτό. */}
            {isChanging && (
                <EmailChangeDialog
                    currentEmail={email}
                    onClose={closeEmailChange}
                    onRequested={handleEmailChangeRequested}
                />
            )}
        </div>
    );
}
